import { Agent, type AgentState } from "@earendil-works/pi-agent-core";
import { registerBuiltInApiProviders, type TextContent } from "@earendil-works/pi-ai";
import {
	ChatPanel,
	createExtractDocumentTool,
	createJavaScriptReplTool,
	defaultConvertToLlm,
	getAppStorage,
	type SessionMetadata,
} from "@earendil-works/pi-web-ui";
import { LitElement, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { getAllowedModels, loadAllowedModels } from "../llm/models.js";
import { pruneContextMessages } from "../llm/prune-context.js";
import { sparkModel, SPARK_PROVIDER, SPARK_CONTEXT_WINDOW } from "../spark-model.js";
import { loadMcpTools } from "../tools/mcp-tools.js";

registerBuiltInApiProviders();

const SYSTEM_PROMPT_BASE = "You are the team assistant. Be concise.";

@customElement("chat-tab")
export class ChatTab extends LitElement {
	createRenderRoot() {
		return this;
	}

	@state() private ready = false;
	@state() private sessions: SessionMetadata[] = [];
	@state() private currentId?: string;
	@state() private currentTitle = "";
	@state() private sidebarOpen = readSidebarPref();

	@query(".chat-slot") private chatSlot?: HTMLDivElement;

	private agent?: Agent;
	private chatPanel?: ChatPanel;
	private agentUnsubscribe?: () => void;
	private mcpTools: Awaited<ReturnType<typeof loadMcpTools>> = [];

	async connectedCallback() {
		super.connectedCallback();
		this.classList.add("flex", "flex-1", "min-h-0");

		const existing = await getAppStorage().providerKeys.get(SPARK_PROVIDER);
		if (!existing) await getAppStorage().providerKeys.set(SPARK_PROVIDER, "proxied-by-backend");

		// Populate the curated model list (Spark + per-user extras) before any
		// panel mounts, so the patched ModelSelector has data on first open.
		await loadAllowedModels();
		this.mcpTools = await loadMcpTools("chat");
		await this.refreshSessions();
		this.ready = true;
		// Wait for the slot div to render, then mount the first session.
		await this.updateComplete;
		await this.newSession();
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		this.agentUnsubscribe?.();
	}

	private async refreshSessions() {
		this.sessions = await getAppStorage().sessions.getAllMetadata();
		this.sessions.sort((a, b) => b.lastModified.localeCompare(a.lastModified));
	}

	private buildAgent(initial?: Partial<AgentState>): Agent {
		const agent = new Agent({
			initialState: {
				systemPrompt:
					initial?.systemPrompt ??
					`${SYSTEM_PROMPT_BASE} Today's date is ${new Date().toISOString().slice(0, 10)}.`,
				// Default to the server's current model list, not the hardcoded
				// sparkModel() fallback — after a model swap on the Spark the
				// old id would otherwise stay preselected forever.
				model: initial?.model ?? getAllowedModels()[0] ?? sparkModel(),
				thinkingLevel: initial?.thinkingLevel ?? "off",
				// Tools are wired through ChatPanel.setAgent's toolsFactory below;
				// anything we put here is overwritten by ChatPanel anyway.
				tools: initial?.tools ?? [],
				messages: initial?.messages ?? [],
			},
			// Without this the agent passes `role: "user-with-attachments"`
			// straight to the LLM, pi-ai filters it as unknown, and the model
			// never sees the attached PDF text / image bytes — so it claims
			// it can't find the file.
			convertToLlm: defaultConvertToLlm,
		});
		// Trim the oldest messages when a session outgrows the context window,
		// instead of letting the provider 400 the request (issue #2). Reads the
		// window from the agent's *current* model, since users can switch
		// models mid-session now. Stored IndexedDB history stays complete.
		agent.transformContext = async (messages) =>
			pruneContextMessages(
				messages,
				agent.state.model?.contextWindow ?? SPARK_CONTEXT_WINDOW,
				agent.state.model?.maxTokens ?? 8192,
			);
		return agent;
	}

	private async newSession() {
		this.agentUnsubscribe?.();
		this.currentId = undefined;
		this.currentTitle = "";
		this.agent = this.buildAgent();
		this.subscribeAgent();
		await this.mountPanel();
	}

	private async loadSession(id: string) {
		const data = await getAppStorage().sessions.get(id);
		if (!data) return;
		const meta = await getAppStorage().sessions.getMetadata(id);
		this.agentUnsubscribe?.();
		this.currentId = id;
		this.currentTitle = meta?.title ?? data.title ?? "";
		// Saved sessions carry a snapshot of their model object. Remap it onto
		// the *current* allowed list by id so a model that was swapped out on
		// the server (or whose window/name changed) doesn't linger; if its id
		// is gone entirely, fall back to the default model.
		const models = getAllowedModels();
		const savedModel = data.model ? models.find((m) => m.id === data.model.id) : undefined;
		this.agent = this.buildAgent({
			model: savedModel ?? models[0],
			thinkingLevel: data.thinkingLevel,
			messages: data.messages,
		});
		this.subscribeAgent();
		await this.mountPanel();
	}

	/**
	 * Build a fresh ChatPanel for the current agent and append it into the
	 * stable `.chat-slot` div. We deliberately avoid putting `${this.chatPanel}`
	 * inside Lit's template — that caused the message area to lose its
	 * agent-interface subscription whenever sidebar state re-rendered.
	 *
	 * Mount the panel BEFORE calling setAgent so the inner agent-interface's
	 * connectedCallback fires in the normal order: it has its session set,
	 * subscribes to agent events, and is correctly in-DOM for the resize
	 * observers and scroll-container queries.
	 */
	private async mountPanel() {
		if (!this.chatSlot || !this.agent) return;
		const panel = new ChatPanel();
		// Swap previous panel out, new panel in — empty initially.
		this.chatSlot.replaceChildren(panel);
		this.chatPanel = panel;
		await panel.setAgent(this.agent, {
			// runtimeProvidersFactory hands the JS REPL tool a way to read
			// attachments and artifacts at execution time. Without it,
			// `readTextAttachment(id)` etc. return nothing and the model
			// can't access uploaded files from a code cell.
			toolsFactory: (_agent, _agentInterface, _artifactsPanel, runtimeProvidersFactory) => {
				const repl = createJavaScriptReplTool();
				repl.runtimeProvidersFactory = runtimeProvidersFactory;
				return [createExtractDocumentTool(), repl, ...this.mcpTools];
			},
		});
	}

	private subscribeAgent() {
		if (!this.agent) return;
		this.agentUnsubscribe = this.agent.subscribe((event) => {
			if (event.type === "message_end" || event.type === "agent_end") {
				// pi-agent-core pushes new messages onto state.messages in place.
				// Lit's identity check on <message-list .messages=${...}> then
				// skips the re-render — and AgentInterface clears its streaming
				// pane assuming the message has landed in MessageList, which
				// makes the just-completed turn vanish. Reassigning forces the
				// state's setter to copy and gives Lit a fresh reference.
				if (this.agent) {
					this.agent.state.messages = [...this.agent.state.messages];
				}
				void this.persistIfReady();
			}
			if (event.type === "agent_end") {
				// AgentInterface schedules its post-run re-render from inside the
				// agent_end listener — but the agent only flips state.isStreaming
				// to false in `finishRun()`, which runs *after* listeners settle.
				// Lit's queued microtask therefore re-binds <message-editor
				// .isStreaming=true>, gating Enter until the next render. Wait
				// for the run to actually idle, then poke the agent-interface so
				// the editor unlocks.
				void this.nudgeAgentInterfaceAfterIdle();
			}
		});
	}

	private async nudgeAgentInterfaceAfterIdle() {
		const agent = this.agent;
		const panel = this.chatPanel;
		if (!agent || !panel) return;
		await agent.waitForIdle();
		// ChatPanel keeps a reference to the inner agent-interface element.
		const ai = (panel as unknown as { agentInterface?: { requestUpdate(): void } })
			.agentInterface;
		ai?.requestUpdate();
	}

	private async persistIfReady() {
		if (!this.agent) return;
		const messages = this.agent.state.messages;
		const hasUser = messages.some((m) => (m as { role?: string }).role === "user");
		const hasAssistant = messages.some((m) => (m as { role?: string }).role === "assistant");
		if (!hasUser || !hasAssistant) return;

		if (!this.currentId) this.currentId = crypto.randomUUID();
		if (!this.currentTitle) this.currentTitle = generateTitle(messages);

		const now = new Date().toISOString();
		const existing = await getAppStorage().sessions.getMetadata(this.currentId);
		const meta: SessionMetadata = existing ?? {
			id: this.currentId,
			title: this.currentTitle,
			createdAt: now,
			lastModified: now,
			messageCount: messages.length,
			usage: {
				input: 0,
				output: 0,
				cacheRead: 0,
				cacheWrite: 0,
				totalTokens: 0,
				cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0, total: 0 },
			},
			thinkingLevel: this.agent.state.thinkingLevel,
			preview: generateTitle(messages),
		};
		meta.title = this.currentTitle;
		meta.lastModified = now;
		meta.messageCount = messages.length;
		meta.preview = generateTitle(messages);
		meta.thinkingLevel = this.agent.state.thinkingLevel;

		await getAppStorage().sessions.save(
			{
				id: this.currentId,
				title: this.currentTitle,
				model: this.agent.state.model,
				thinkingLevel: this.agent.state.thinkingLevel,
				messages,
				createdAt: existing?.createdAt ?? now,
				lastModified: now,
			},
			meta,
		);
		await this.refreshSessions();
	}

	private async deleteSession(id: string, ev: Event) {
		ev.stopPropagation();
		if (!confirm("Delete this session?")) return;
		await getAppStorage().sessions.delete(id);
		if (this.currentId === id) await this.newSession();
		await this.refreshSessions();
	}

	private toggleSidebar() {
		this.sidebarOpen = !this.sidebarOpen;
		try {
			localStorage.setItem("chat.sidebarOpen", String(this.sidebarOpen));
		} catch {
			/* ignore quota / privacy mode */
		}
	}

	render() {
		if (!this.ready) {
			return html`<div class="flex-1 grid place-items-center text-muted-foreground">Loading…</div>`;
		}
		return html`
			<div class="flex flex-1 min-h-0">
				${this.sidebarOpen ? this.renderSidebar() : ""}
				<div class="flex-1 min-w-0 flex flex-col">
					${this.renderTitleBar()}
					<!-- Stable mount point. ChatPanel is appended imperatively in
					     mountPanel(); flex-col here is load-bearing so the panel
					     stretches to the slot's full width via the cross-axis. -->
					<div class="chat-slot flex-1 min-h-0 flex flex-col"></div>
				</div>
			</div>
		`;
	}

	private renderSidebar() {
		return html`
			<!-- Sidebar scrolling (issue #4) relies on min-height:0 rules in
			     app.css — the min-h-0 utility class does NOT exist in
			     pi-web-ui's precompiled Tailwind bundle, so classes alone
			     can't fix the flex min-height:auto stretching here. -->
			<aside class="w-64 shrink-0 border-r border-border flex flex-col min-h-0">
				<div class="p-3 border-b border-border flex gap-2">
					<button
						class="flex-1 px-3 py-2 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
						@click=${() => this.newSession()}
					>
						+ New chat
					</button>
					<button
						class="px-2 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-accent"
						@click=${() => this.toggleSidebar()}
						title="Hide sidebar"
					>
						«
					</button>
				</div>
				<div class="flex-1 overflow-y-auto">
					${this.sessions.length === 0
						? html`<div class="p-4 text-sm text-muted-foreground">No sessions yet.</div>`
						: this.sessions.map(
								(s) => html`
									<div
										class="p-3 border-b border-border cursor-pointer hover:bg-accent group ${this.currentId === s.id ? "bg-accent" : ""}"
										@click=${() => this.loadSession(s.id)}
									>
										<div class="flex justify-between items-start gap-2">
											<div class="font-medium text-sm truncate" title=${s.title}>${s.title}</div>
											<button
												class="text-xs text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100"
												@click=${(e: Event) => this.deleteSession(s.id, e)}
												title="Delete"
											>
												✕
											</button>
										</div>
										<div class="text-xs text-muted-foreground mt-1">
											${s.messageCount} msg · ${formatRelativeTime(s.lastModified)}
										</div>
									</div>
								`,
							)}
				</div>
			</aside>
		`;
	}

	private renderTitleBar() {
		return html`
			<div class="px-3 py-2 border-b border-border flex items-center gap-2 text-sm">
				${!this.sidebarOpen
					? html`
							<button
								class="px-2 py-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
								@click=${() => this.toggleSidebar()}
								title="Show sidebar"
							>
								»
							</button>
							<button
								class="px-2 py-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
								@click=${() => this.newSession()}
								title="New chat"
							>
								+
							</button>
						`
					: ""}
				<span class="font-medium text-foreground truncate">${this.currentTitle || "New chat"}</span>
				${this.currentId
					? html`<span class="ml-2 text-xs text-muted-foreground">· saved</span>`
					: ""}
			</div>
		`;
	}
}

function readSidebarPref(): boolean {
	try {
		const v = localStorage.getItem("chat.sidebarOpen");
		if (v === null) return true;
		return v === "true";
	} catch {
		return true;
	}
}

function generateTitle(messages: ReadonlyArray<unknown>): string {
	const first = messages.find((m) => (m as { role?: string }).role === "user") as
		| { content?: string | TextContent[] }
		| undefined;
	if (!first) return "";
	const content = first.content;
	let text = "";
	if (typeof content === "string") text = content;
	else if (Array.isArray(content)) {
		text = content
			.filter((c): c is TextContent => (c as { type?: string }).type === "text")
			.map((c) => c.text ?? "")
			.join(" ");
	}
	text = text.trim();
	if (!text) return "(empty)";
	const sentenceEnd = text.search(/[.!?]/);
	if (sentenceEnd > 0 && sentenceEnd <= 50) return text.substring(0, sentenceEnd + 1);
	return text.length <= 50 ? text : `${text.substring(0, 47)}…`;
}

function formatRelativeTime(iso: string): string {
	const t = new Date(iso).getTime();
	const diff = Date.now() - t;
	if (diff < 60_000) return "just now";
	if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m`;
	if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h`;
	if (diff < 7 * 86_400_000) return `${Math.round(diff / 86_400_000)}d`;
	return new Date(iso).toLocaleDateString();
}
